import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type { EnquiryStage } from '@/lib/sales-enquiry-stages'

export type ContactEnquiry = {
  id: number
  name: string
  email: string
  phone: string
  message: string
  is_read: boolean
  created_at: string | null
  service_instance_id: string | null
  stage: EnquiryStage | string
  stage_label: string
  stage_updated_at: string | null
  customer_user_id?: number | null
  customer_user_link_id?: string | number | null
  customer_user_group_id?: number | null
  customer_name?: string | null
  customer_group_code?: string | null
  customer_group_title?: string | null
}

export type EnquiriesListResponse = {
  results: ContactEnquiry[]
  count: number
}

export type SalesFollowupTask = {
  id: number
  parent_followup_id: number | null
  assignee_user_id: number | null
  activity_type: string
  title: string
  notes: string
  status: 'open' | 'completed' | 'cancelled'
  priority: 'low' | 'medium' | 'high'
  due_at: string | null
  completed_at: string | null
  assignee_name: string
  assignee_email: string
  outcome_note: string
  enquiry_id: number | null
  enquiry_name: string | null
  enquiry_email: string | null
  enquiry_phone: string | null
  service_instance_id: string | null
  created_at: string
  updated_at: string
  child_count?: number
  is_editable?: boolean
}

export type FollowupListResponse = {
  results: SalesFollowupTask[]
  count: number
}

export type CreateEnquiryCustomerPayload = {
  customer_group?: 'retail' | 'business' | 'retail_customer' | 'business_customer'
  user_group?: string | number | null
}

export type CreateEnquiryPayload = {
  service_instance_id?: string | null
  name: string
  email?: string
  phone?: string
  message?: string
}

export type CreateFollowupPayload = {
  service_instance_id?: string | null
  enquiry_id?: number | null
  parent_followup_id?: number | null
  assignee_user_id?: number | null
  activity_type?: string
  title: string
  notes?: string
  priority?: 'low' | 'medium' | 'high'
  due_at?: string | null
  assignee_name?: string
  assignee_email?: string
}

export type UpdateFollowupPayload = Partial<Omit<CreateFollowupPayload, 'service_instance_id' | 'enquiry_id'>>

export const salesApi = {
  async listEnquiries(params?: {
    service_instance_id?: string | null
    stage?: string | null
    customer_user_id?: string | number | null
  }): Promise<EnquiriesListResponse> {
    const response = await api.get<EnquiriesListResponse>(API_PATHS.sales.enquiries.list, { params })
    return response.data
  },

  async updateEnquiryStage(id: number, stage: EnquiryStage | string): Promise<Pick<ContactEnquiry, 'stage' | 'stage_label' | 'stage_updated_at'>> {
    const response = await api.patch<Pick<ContactEnquiry, 'stage' | 'stage_label' | 'stage_updated_at'>>(
      API_PATHS.sales.enquiries.updateStage(id),
      { stage },
    )
    return response.data
  },

  async markEnquiryRead(id: number): Promise<{ id: number; is_read: boolean }> {
    const response = await api.post<{ id: number; is_read: boolean }>(API_PATHS.sales.enquiries.markRead(id))
    return response.data
  },

  async createEnquiry(payload: CreateEnquiryPayload): Promise<ContactEnquiry> {
    const response = await api.post<ContactEnquiry>(API_PATHS.sales.enquiries.staffCreate, payload)
    return response.data
  },

  async createCustomerFromEnquiry(
    id: number,
    payload: CreateEnquiryCustomerPayload = {},
    serviceInstanceId?: string | null,
  ): Promise<ContactEnquiry> {
    const response = await api.post<ContactEnquiry>(
      API_PATHS.sales.enquiries.createCustomer(id),
      payload,
      {
        params: {
          service_instance_id: serviceInstanceId || undefined,
        },
      },
    )
    return response.data
  },

  async listFollowups(params?: {
    service_instance_id?: string | null
    status?: string
    priority?: string
    bucket?: 'overdue' | 'today' | 'upcoming'
    enquiry_id?: number | null
  }): Promise<FollowupListResponse> {
    const response = await api.get<FollowupListResponse>(API_PATHS.sales.followups.list, { params })
    return response.data
  },

  async createFollowup(payload: CreateFollowupPayload): Promise<SalesFollowupTask> {
    const response = await api.post<SalesFollowupTask>(API_PATHS.sales.followups.create, payload)
    return response.data
  },

  async updateFollowup(id: number, payload: UpdateFollowupPayload): Promise<SalesFollowupTask> {
    const response = await api.patch<SalesFollowupTask>(API_PATHS.sales.followups.update(id), payload)
    return response.data
  },

  async completeFollowup(id: number, outcomeNote?: string): Promise<SalesFollowupTask> {
    const response = await api.post<SalesFollowupTask>(API_PATHS.sales.followups.complete(id), { outcome_note: outcomeNote ?? '' })
    return response.data
  },

  async deleteFollowup(id: number): Promise<void> {
    await api.post(API_PATHS.sales.followups.delete(id))
  },
}
